import type { GbpPostType } from '@pageboss/shared';
import { SegmentedControl } from '@/components/inspector/segmented-control';
import { fieldClasses } from './post-field-styles';

// Publish-now / schedule toggle + the schedule fields. Now / Schedule is one
// choice out of two, so it is the shared SegmentedControl — same primitive
// the post-type row above it uses (design audit T14, T7's parked list).
// Repeat renders ONLY for Update — the shared schema refuses it elsewhere,
// and hiding it beats a post-submit error.
//
// Screen-design addendum §B: a visible "When to publish" eyebrow label
// (review-reply-box.tsx's "Your reply" idiom) sits above the toggle — an
// Event card stacks this Date/Time grid directly above the Event Starts/Ends
// grid, and unlabeled the two read as one continuous set of date fields.
//
// Split out of post-compose.tsx to keep that file readable (CLAUDE.md:
// components under ~200 lines).
const WHEN_OPTIONS = [
  { value: 'now', label: 'Publish now' },
  { value: 'schedule', label: 'Schedule' },
] as const;

export function PostScheduleRow(props: {
  type: GbpPostType;
  when: 'now' | 'schedule';
  setWhen: (v: 'now' | 'schedule') => void;
  schedDate: string;
  setSchedDate: (v: string) => void;
  schedTime: string;
  setSchedTime: (v: string) => void;
  repeat: '' | 'weekly' | 'monthly';
  setRepeat: (v: '' | 'weekly' | 'monthly') => void;
  repeatUntil: string;
  setRepeatUntil: (v: string) => void;
}) {
  const {
    type,
    when,
    setWhen,
    schedDate,
    setSchedDate,
    schedTime,
    setSchedTime,
    repeat,
    setRepeat,
    repeatUntil,
    setRepeatUntil,
  } = props;
  return (
    <div className="flex flex-col gap-3">
      <span className="eyebrow">
        When to publish
      </span>
      <SegmentedControl<'now' | 'schedule'>
        ariaLabel="When to publish"
        value={when}
        options={WHEN_OPTIONS}
        onChange={setWhen}
      />

      {when === 'schedule' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="post-sched-date" className="text-sm font-medium text-foreground">
              Date
            </label>
            <input
              id="post-sched-date"
              type="date"
              required
              value={schedDate}
              onChange={(e) => setSchedDate(e.target.value)}
              className={fieldClasses}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="post-sched-time" className="text-sm font-medium text-foreground">
              Time
            </label>
            <input
              id="post-sched-time"
              type="time"
              required
              value={schedTime}
              onChange={(e) => setSchedTime(e.target.value)}
              className={fieldClasses}
            />
          </div>
          {type === 'update' ? (
            <>
              <div className="flex flex-col gap-1">
                <label htmlFor="post-repeat" className="text-sm font-medium text-foreground">
                  Repeat
                </label>
                <select
                  id="post-repeat"
                  value={repeat}
                  onChange={(e) => setRepeat(e.target.value as '' | 'weekly' | 'monthly')}
                  className={fieldClasses}
                >
                  <option value="">Doesn't repeat</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {repeat ? (
                <div className="flex flex-col gap-1">
                  <label htmlFor="post-repeat-until" className="text-sm font-medium text-foreground">
                    Repeat until (optional)
                  </label>
                  <input
                    id="post-repeat-until"
                    type="date"
                    value={repeatUntil}
                    onChange={(e) => setRepeatUntil(e.target.value)}
                    aria-describedby="post-repeat-until-hint"
                    className={fieldClasses}
                  />
                  <span id="post-repeat-until-hint" className="text-xs text-text-tertiary">
                    Left empty, it repeats for up to a year.
                  </span>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
